// --- categories.ts ----------------------------------------------------------

/** Canonical categories for your UI tabs */
export type Category = 'reasoning' | 'visual' | 'classical' | 'audio';

/** Minimal shape of your upstream API model items (from modelsConfig.json). */
export interface ApiModel {
  model_id: string;
  active?: boolean;
  owned_by?: string | null;
  provider_type?: string;
  tasks?: string[];
  description?: string;
  context_window?: number | string | null;
  region?: string | null;
}

/** Manual curation entry with a rank (lower rank == higher placement). */
export interface CuratedEntry {
  modelId: string;
  rank: number;
  note?: string;
}

/** The curation “sidecar” keyed by category. */
export type CuratedCatalog = Record<Category, CuratedEntry[]>;

/**
 * Your manual picks come first. Keep these short and opinionated.
 * rank: 1,2,3... gives the exact order you want to show in UI.
 */
export const curatedCatalog: CuratedCatalog = {
  reasoning: [
    {
      modelId: 'gemini-2.0-flash',
      rank: 1,
    },
    {
      modelId: 'DeepSeek-R1-Distill-Llama-70B',
      rank: 2
    },
    {
      modelId: 'openai/gpt-oss-120b',
      rank: 3
    },
    {
      modelId: 'groq/compound',
      rank: 4
    },
    {
      modelId: 'mistral-small-latest',
      rank: 5
    },
    {
      modelId: 'ibm/granite-3-2-8b-instruct',
      rank: 6
    },
    {
      modelId: 'meta-llama/llama-4-scout-17b-16e-instruct',
      rank: 7
    } // has reasoning toggle in desc
  ],
  visual: [
    {
      modelId: 'meta-llama/llama-3-2-90b-vision-instruct',
      rank: 1
    },
    {
      modelId: 'meta-llama/llama-3-2-11b-vision-instruct',
      rank: 2
    },
    {
      modelId: 'mistralai/mistral-medium-2505',
      rank: 3,
      note: 'multimodal'
    },
    {
      modelId: 'meta-llama/llama-guard-3-11b-vision',
      rank: 4,
      note: 'safety-vision'
    },
    {
      modelId: 'qwen3-8b-w8a8',
      rank: 5,
      note: 'multimodal'
    },
  ],
  classical: [
    {
      modelId: 'llama-3.3-70b-versatile',
      rank: 1
    },
    {
      modelId: 'meta-llama/llama-3-3-70b-instruct',
      rank: 2
    },
    {
      modelId: 'meta-llama/llama-3-405b-instruct',
      rank: 3
    },
    {
      modelId: 'qwen/qwen3-32b',
      rank: 4
    },
    {
      modelId: 'moonshotai/kimi-k2-instruct-0905',
      rank: 5
    },
    {
      modelId: 'ibm/granite-3-8b-instruct',
      rank: 6
    },
    {
      modelId: 'ibm/granite-3-3-8b-instruct',
      rank: 7
    },
  ],
  audio: [
    {
      modelId: 'whisper-large-v3',
      rank: 1
    },
    {
      modelId: 'whisper-large-v3-turbo',
      rank: 2
    },
    {
      modelId: 'Whisper-Large-v3',
      rank: 3
    }, // SambaNova namespace
    {
      modelId: 'playai-tts',
      rank: 4
    },
    {
      modelId: 'playai-tts-arabic',
      rank: 5
    },
  ],
};

/**
 * Heuristics to AUTO-CLASSIFY remaining models into categories
 * when they are not in curatedCatalog.
 */
export function inferCategory(m: ApiModel): Category | null {
  const id = m.model_id.toLowerCase();
  const tasks = (m.tasks || []).map(t => t.toLowerCase());
  const desc = (m.description || '').toLowerCase();

  // Audio
  if (
    id.includes('whisper') ||
    id.includes('tts') ||
    tasks.includes('audio_transcription') ||
    desc.includes('speech') ||
    desc.includes('asr')
  ) return 'audio';

  // Visual (multimodal / vision)
  if (
    id.includes('vision') ||
    id.includes('maverick') ||
    desc.includes('multimodal') ||
    desc.includes('image') ||
    desc.includes('vision') ||
    id.includes('qwen3-8b-w8a8') // exceptional case
  ) return 'visual';

  // Reasoning (explicit reasoning families)
  if (
    id.includes('r1') || // DeepSeek-R1*
    id.includes('reason') ||
    desc.includes('reasoning') ||
    id.includes('gpt-oss')
  ) return 'reasoning';

  // Classical fallback
  return 'classical';
}

/**
 * Tie-breaker rank for non-curated entries.
 * We keep curation on top (rank 1..N). The rest start at 1000+ based on
 * some deterministic rules so order is stable across renders.
 */
function deriveFallbackRank(m: ApiModel): number {
  // Prefer active models, then longer context, then alphabetical
  const activeBias = m.active ? 0 : 50;
  const ctx = typeof m.context_window === 'number'
    ? 131072 - m.context_window
    : 0;
  // Small constant to keep stable but provider-aware ordering
  const providerWeight = providerOrder(m.provider_type);
  return 1000 + activeBias + providerWeight + Math.max(0, ctx);
}

function providerOrder(p?: string): number {
  const order = [
    'OpenAI', 'Meta', 'IBM',
    'Groq', 'SambaNova', 'PlayAI',
    'OnPrem' // treat on-prem as last by default (change if you prefer)
  ];
  const idx = order.findIndex(x => (p || '').toLowerCase() === x.toLowerCase());
  return idx === -1 ? 40 : idx * 2; // small spread
}

/**
 * Build the final category → ranked list for UI:
 * - Inserts your manual picks first (by rank).
 * - Appends all other models inferred into the right category with fallback ranks.
 * - Dedupes across providers if the same model_id appears more than once in the feed.
 *
 * You can still filter by provider in the UI AFTER this step.
 */
export function buildRankedCatalog(models: ApiModel[], curation = curatedCatalog) {
  // Normalize curated sets
  const curatedIdx = new Map<string, { category: Category; rank: number }>();
  (Object.keys(curation) as Category[]).forEach(cat => {
    curation[cat]
      .sort((a, b) => a.rank - b.rank)
      .forEach(entry => curatedIdx.set(entry.modelId, {
        category: cat,
        rank: entry.rank
      }));
  });

  // Prepare buckets with curated entries prefilled (ensures your top picks are first)
  const out: Record<Category, { modelId: string; rank: number }[]> = {
    reasoning: [...curation.reasoning],
    visual: [...curation.visual],
    classical: [...curation.classical],
    audio: [...curation.audio],
  } as Record<Category, { modelId: string; rank: number }[]>;

  const seen = new Set<string>(
    (Object.values(curation).flat().map(x => x.modelId))
  );

  // Add all other models
  for (const m of models) {
    if (!m?.model_id) continue;
    if (seen.has(m.model_id)) continue; // already curated in this pass
    const cat = inferCategory(m);
    if (!cat) continue;
    out[cat].push({
      modelId: m.model_id,
      rank: deriveFallbackRank(m)
    });
    seen.add(m.model_id);
  }

  // Stable sort and return
  (Object.keys(out) as Category[]).forEach(cat => {
    out[cat].sort((a, b) => a.rank - b.rank || a.modelId.localeCompare(b.modelId));
  });

  return out;
}

/**
 * Optional: shape that’s convenient for your UI mapping:
 * [
 *   { category: "reasoning", models: [{modelId, rank}, ...] },
 *   ...
 * ]
 */
export function toUiArray(catalog: ReturnType<typeof buildRankedCatalog>) {
  return (Object.keys(catalog) as Category[]).map(category => ({
    category,
    models: catalog[category],
  }));
}
